import { push, replace } from 'connected-react-router';
// lodash
import get from 'lodash/get';
import has from 'lodash/has';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import includes from 'lodash/includes';
//
import routes from '../../../constants/routeConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import {
  getPaymentPageAfterSubmitOrderError,
  initPaymentPage,
  reserveStock,
} from './orderFormActions';
import {
  handleInvalidPartnerDiscountCardNumber,
} from '../partner-discount/partnerDiscountActions';
import {
  APPS_CLEANUP_BASKET,
  RESERVE_STOCK,
  RESET_SUBMIT_ORDER_ERROR,
  SAVE_ACCOUNT_DATA,
  SHOW_POS_CREDIT_SESSION_EXPIRED_ERROR,
  SUBMIT_ORDER,
  UPDATE_BILLING_ADDRESS,
  PUT_CC_DELIVERY_DETAILS_SILENTLY,
} from '../../../constants/actionConstants';
import {
  URL_CARD_PAYMENT_BILLING_ADDRESS,
  URL_POST_ORDER_ACCOUNT_DATA,
  URL_SUBMIT_ORDERS,
  URL_SUBMIT_ORDERS_POS_CREDIT,
} from '../../../constants/endpointConstants';
import {
  SAVE_AS_DEFAULT_FORM_ID,
} from '../../../utils/form/configs/saveAsDefault';
import { triggerAnalyticsForUncompletedActions } from '../analytics/analyticsAction';
import { isApps, isPactApp } from '../../reducers/app/appReducer';
import getPathWithQueryString from '../../../utils/helpers/url';
import queryStrings from '../../../constants/queryStringConstants';
import getIovationBlackBoxIdFromDom from '../../../utils/iovation/getIovationBlackBoxIdFromDom';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import handleInconsistentOrderFormState from '../../../utils/orderform/handleInconsistentOrderFormState';
import { handbackTo } from '../data-action-link/dataActionLinkActions';
import { creditApplicationStatuses } from '../../../constants/posCreditConstants';
import {
  collectionCutOffPassedDuringSubmitOrder,
  handleCutOffPassedModalChangeDateClick,
} from '../click-and-collect/clickAndCollectActions';
import { auth0Login } from '../bff/bffActions';

function isValidSaveNewCardValue(value) {
  return value === 'true' || value === 'false';
}

export function getSaveAccountDataBody(state) {

  const removeExpiredCards = !!get(state, 'user.removedDefaultPaymentCard');
  const formValues = get(state, `form.${SAVE_AS_DEFAULT_FORM_ID}.values`, {});
  const saveAddressAsDefaultId = get(state.addressBook, 'addressIdToBeSavedAsDefault');
  const saveCollectionPointAsDefaultId = formValues.saveCollectionPointAsDefault;
  const saveCardAsDefaultId = formValues.savePaymentAsDefault;

  const selectedPaymentType = get(state, 'payment.selectedPaymentType', '');

  let shouldSaveWithNewAddress = false;
  let billingAddressId;
  const parsedSelectedPaymentType = selectedPaymentType.split(':');
  const selectedPaymentCardId = parsedSelectedPaymentType[1] || '';

  if (selectedPaymentType) {
    const selectedPaymentTypeName = parsedSelectedPaymentType[0];

    if (selectedPaymentTypeName === paymentTypeConstants.SAVED_CREDIT_CARD) {
      const savedCards = get(state, 'user.savedPaymentCards');
      const savedCardAddressId = find(savedCards, { id: selectedPaymentCardId }).addressId;
      billingAddressId = get(state, 'user.selectedBillingAddress.id');

      shouldSaveWithNewAddress = !isEqual(savedCardAddressId, billingAddressId);
    }
  }

  function getId(id) {
    if (!id || isValidSaveNewCardValue(id)) {
      return '';
    }
    return id;
  }

  function getDefault(id) {
    return !!(id && id !== 'false');
  }

  return {
    checkoutPreferences: {
      address: {
        id: getId(saveAddressAsDefaultId),
        default: getDefault(saveAddressAsDefaultId),
      },
      collectionPoint: {
        id: getId(saveCollectionPointAsDefaultId),
        default: getDefault(saveCollectionPointAsDefaultId),
      },
      paymentCard: {
        id: getId(saveCardAsDefaultId) || selectedPaymentCardId,
        default: getDefault(saveCardAsDefaultId),
        ...shouldSaveWithNewAddress && { addressId: billingAddressId },
      },
    },
    removeExpiredCards,
  };

}

export const resetSubmitOrderError = () => async (dispatch, getState) => {

  const state = getState();
  const searchParams = new URLSearchParams(get(state, 'router.location.search'));
  const query = Object.fromEntries(searchParams);

  const clearQuery = Object.keys(query).length && !(has(query, '3dsecure') && query['3dsecure'] === '');

  if (clearQuery) {
    // console.warn('RESET SUBMIT ORDER ERROR: Replacing to Payment');
    dispatch(replace(routes.PAYMENT));
  }

  dispatch({
    type: RESET_SUBMIT_ORDER_ERROR,
  });
};

// TODO why do we use this here instead of using the almost identical function from cardPaymentActions?
export const updateBillingAddress = body => ({
  type: UPDATE_BILLING_ADDRESS,
  request: client => client({ path: URL_CARD_PAYMENT_BILLING_ADDRESS, config: { method: 'PUT', body } }),
});

export const submitOrder = (state, isPosCreditOrder) => {
  const url = isPosCreditOrder ? URL_SUBMIT_ORDERS_POS_CREDIT : URL_SUBMIT_ORDERS;
  const selectedPaymentType = get(state, 'payment.selectedPaymentType');

  const submitOrderDispatch = {
    type: SUBMIT_ORDER,
    request: client => client({
      path: url,
      config: { method: 'POST', headers: { 'JL-DEVICE-ID': getIovationBlackBoxIdFromDom().ioBB } },
    }),
    ...selectedPaymentType && { selectedPaymentType },
  };

  if (isPactApp(state)) {
    submitOrderDispatch.isPactApp = true;
  }

  return submitOrderDispatch;
};

export const saveAccountData = (state) => {

  const body = getSaveAccountDataBody(state);

  return {
    type: SAVE_ACCOUNT_DATA,
    request: client => client({ path: URL_POST_ORDER_ACCOUNT_DATA, config: { method: 'POST', body } }),
    isApps: isApps(state),
  };

};

const showPosCreditSessionExpiredError = (isInvalidPartnerDiscount, isLoginRequired) => ({
  type: SHOW_POS_CREDIT_SESSION_EXPIRED_ERROR,
  isInvalidPartnerDiscount,
  isLoginRequired,
});

export const submitOrderAndRedirect = (billingAddress, isPosCreditOrder) => async (dispatch, getState) => {
  if (billingAddress) {
    const { type } = await dispatch(updateBillingAddress(billingAddress));
    if (type === `${UPDATE_BILLING_ADDRESS}.FAILED`) {
      return;
    }
  }

  // handle stock reservation scenario where PACT customer has paid entirely with gift card/voucher
  if (isPactApp(getState()) && !getState().orderForm.reserveStockApiCallComplete) {
    const reserveStockResponse = await dispatch(reserveStock());
    if (reserveStockResponse.type === `${RESERVE_STOCK}.FAILED`) {
      if (reserveStockResponse.error?.code === errorCodeConstants.PARTNER_CARD_NUMBER_INVALID) {
        await dispatch(handleInvalidPartnerDiscountCardNumber());
      } else {
        await dispatch(initPaymentPage());
      }
      return;
    }
  }

  const state = getState();

  // push to /payment?processing so that if user navigates back we can keep them on payment page
  if (!isPosCreditOrder) {
    // POS credit order already pushed to ?poscredit in posCreditPaymentAction
    const path = getPathWithQueryString(routes.PAYMENT, queryStrings.SUBMITTING_ORDER);
    dispatch(push(path));
  }

  if (state.clickAndCollect?.cutOffPassed && !isPosCreditOrder) {
    const newCollectionDayResponse = await dispatch(collectionCutOffPassedDuringSubmitOrder());
    if (newCollectionDayResponse.type === `${PUT_CC_DELIVERY_DETAILS_SILENTLY}.FAILED`) {
      dispatch(handleCutOffPassedModalChangeDateClick({
        isPayment: true,
        modalVisible: false,
      }));

      return;
    }
  }

  const submitOrderAction = await dispatch(submitOrder(state, isPosCreditOrder));

  if (submitOrderAction.type === `${SUBMIT_ORDER}.SUCCESS`) {
    if (includes([creditApplicationStatuses.DECLINED, creditApplicationStatuses.REFERRED, creditApplicationStatuses.APPROVED, creditApplicationStatuses.CANCELLED], get(submitOrderAction, 'result.status', ''))) {
      const browseURL = get(state, 'bff.browseUrl');
      await dispatch({ type: APPS_CLEANUP_BASKET });
      dispatch(handbackTo(browseURL));
      return;
    }
    dispatch(push(routes.ORDER_CONFIRMATION));

    // FIXME: Should this happen after successful call to getOrderConfirmationPage()?
    if (get(state, 'user.isSignedIn')) {
      const response = await dispatch(saveAccountData(state));
      const uncompletedActions = get(response, 'result.uncompletedActions');

      if (response.type === `${SAVE_ACCOUNT_DATA}.SUCCESS` && uncompletedActions) {
        dispatch(triggerAnalyticsForUncompletedActions(uncompletedActions));
      }
    }

  }

  if (submitOrderAction.type === `${SUBMIT_ORDER}.FAILED`) {
    const { error: { code, status } } = submitOrderAction;

    if (code === errorCodeConstants.CLIENT_PACT_ORDER_ERROR) {
      return;
    }

    if (status === 401) {
      if (code === errorCodeConstants.PAYMENT_POS_CREDIT_DECLINED_ERROR) {
        await dispatch(auth0Login());
        return;
      }

      if (includes(submitOrderAction.selectedPaymentType, paymentTypeConstants.POS_CREDIT)) {
        const isInvalidPartnerDiscount = code === errorCodeConstants.PARTNER_CARD_NUMBER_INVALID;
        const isLoginRequired = code === errorCodeConstants.PAYMENT_POS_CREDIT_CANCELLED_ERROR
          || code === errorCodeConstants.PAYMENT_POS_CREDIT_APPLICATION_INCOMPLETE;

        await dispatch(showPosCreditSessionExpiredError(isInvalidPartnerDiscount, isLoginRequired));
        return;
      }
    }

    if (code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE) {
      await dispatch(handleInconsistentOrderFormState());
      return;
    }

    if (code === errorCodeConstants.PARTNER_CARD_NUMBER_INVALID) {
      await dispatch(handleInvalidPartnerDiscountCardNumber());
      return;
    }

    await dispatch(replace(routes.PAYMENT));

    if (code === errorCodeConstants.GATEWAY_TIMEOUT_ERROR || code === errorCodeConstants.CLIENT_CONNECTIVITY_ERROR) {
      dispatch({
        type: `${SUBMIT_ORDER}.TIMEOUT`,
      });
      return;
    }

    dispatch(getPaymentPageAfterSubmitOrderError(submitOrderAction));

  }

};
